import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { api } from "../api";

export default function Testimonials() {
  const [testimonials, setTestimonials] = useState([]);
  const [current, setCurrent] = useState(0);
  const [dragging, setDragging] = useState(false);
  const dragStart = useRef(null);

  useEffect(() => {
    api.getTestimonials().then(setTestimonials).catch(() => {});
  }, []);

  const total = testimonials.length;
  const prev = () => setCurrent(c => (c - 1 + total) % total);
  const next = () => setCurrent(c => (c + 1) % total);

  // Auto-advance every 6 seconds
  useEffect(() => {
    if (total < 2) return;
    const timer = setInterval(next, 6000);
    return () => clearInterval(timer);
  }, [total, current]);

  // Drag / swipe
  const onDragStart = (e) => {
    dragStart.current = e.touches ? e.touches[0].clientX : e.clientX;
    setDragging(true);
  };
  const onDragEnd = (e) => {
    if (!dragging || dragStart.current === null) return;
    const endX = e.changedTouches ? e.changedTouches[0].clientX : e.clientX;
    const diff = dragStart.current - endX;
    if (Math.abs(diff) > 50) diff > 0 ? next() : prev();
    dragStart.current = null;
    setDragging(false);
  };

  return (
    <div style={{ paddingTop: "5rem" }}>

      {/* Hero */}
      <section style={{ background: "#0B0B0B", padding: "6rem 2rem 5rem" }}>
        <div className="container">
          <p className="section-label" style={{ color: "#95BB83" }}>Testimonials</p>
          <h1 style={{
            fontFamily: "var(--font)", fontWeight: 900,
            fontSize: "clamp(2.8rem, 7vw, 6rem)",
            color: "#fff", lineHeight: 0.95, letterSpacing: "-0.03em",
          }}>
            What People<br /><span style={{ color: "#8A1613" }}>Say.</span>
          </h1>
        </div>
      </section>

      {/* Carousel */}
      <section style={{ background: "#F7F1DF", padding: "6rem 0 5rem" }}>
        {testimonials.length === 0 ? (
          <div style={{ textAlign: "center", padding: "4rem 2rem" }}>
            <p style={{ fontSize: "3rem", marginBottom: "1rem" }}>💬</p>
            <p style={{ color: "rgba(11,11,11,0.4)", fontFamily: "var(--font-body)" }}>
              Testimonials coming soon.
            </p>
          </div>
        ) : (
          <>
            {/* Slide track */}
            <div
              onMouseDown={onDragStart}
              onMouseUp={onDragEnd}
              onTouchStart={onDragStart}
              onTouchEnd={onDragEnd}
              style={{ overflow: "hidden", cursor: dragging ? "grabbing" : "grab", userSelect: "none" }}
            >
              <div style={{
                display: "flex",
                transition: dragging ? "none" : "transform 0.55s cubic-bezier(0.4,0,0.2,1)",
                transform: `translateX(-${current * 100}%)`,
              }}>
                {testimonials.map((t) => (
                  <div key={t.id} style={{
                    minWidth: "100%",
                    padding: "0 2rem",
                    display: "flex",
                    justifyContent: "center",
                  }}>
                    <div style={{
                      background: "#fff",
                      borderRadius: 28,
                      padding: "clamp(2rem, 5vw, 3.5rem)",
                      maxWidth: 760,
                      width: "100%",
                      border: "1px solid rgba(11,11,11,0.07)",
                      boxShadow: "0 12px 48px rgba(0,0,0,0.07)",
                      position: "relative",
                    }}>
                      {/* Watermark quote mark */}
                      <span style={{
                        position: "absolute", top: 20, right: 28,
                        fontFamily: "var(--font)", fontWeight: 900,
                        fontSize: "9rem", color: "#8A1613",
                        opacity: 0.05, lineHeight: 1,
                        userSelect: "none", pointerEvents: "none",
                      }}>"</span>

                      {/* Icon */}
                      <div style={{
                        width: 48, height: 48, borderRadius: "50%",
                        background: "#8A1613",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: "1.2rem", marginBottom: "1.5rem",
                      }}>
                        💬
                      </div>

                      {/* Quote */}
                      <p style={{
                        fontFamily: "var(--font-body)",
                        fontSize: "clamp(1rem, 2.5vw, 1.25rem)",
                        color: "#0B0B0B", lineHeight: 1.85,
                        fontStyle: "italic", marginBottom: "2rem",
                        position: "relative", zIndex: 1,
                      }}>
                        "{t.quote}"
                      </p>

                      {/* Author */}
                      <div style={{
                        display: "flex", alignItems: "center", gap: "1rem",
                        paddingTop: "1.5rem",
                        borderTop: "1px solid rgba(11,11,11,0.08)",
                        flexWrap: "wrap",
                      }}>
                        {t.photo_url
                          ? <img src={t.photo_url} alt={t.author_name}
                              style={{ width: 52, height: 52, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }} />
                          : <div style={{
                              width: 52, height: 52, borderRadius: "50%",
                              background: "linear-gradient(135deg,#8A1613,#95BB83)",
                              display: "flex", alignItems: "center", justifyContent: "center",
                              fontFamily: "var(--font)", fontWeight: 800,
                              color: "#fff", fontSize: "1.2rem", flexShrink: 0,
                            }}>
                              {t.author_name.charAt(0).toUpperCase()}
                            </div>
                        }
                        <div style={{ flex: 1 }}>
                          <p style={{ fontFamily: "var(--font)", fontWeight: 800, fontSize: "1rem", color: "#0B0B0B", margin: 0 }}>
                            {t.author_name}
                          </p>
                          {(t.author_title || t.organisation) && (
                            <p style={{ fontFamily: "var(--font-body)", fontSize: "0.82rem", color: "rgba(11,11,11,0.45)", margin: "0.15rem 0 0" }}>
                              {t.author_title}{t.author_title && t.organisation ? " · " : ""}{t.organisation}
                            </p>
                          )}
                        </div>
                        {t.logo_url && (
                          <img src={t.logo_url} alt={t.organisation}
                            style={{ height: 36, maxWidth: 100, objectFit: "contain", opacity: 0.7 }} />
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Controls */}
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "center",
              gap: "1.5rem", marginTop: "2.5rem", padding: "0 2rem",
            }}>
              {/* Prev arrow */}
              <button onClick={prev} aria-label="Previous" disabled={total < 2} style={{
                width: 48, height: 48, borderRadius: "50%",
                background: "#fff", border: "1px solid rgba(11,11,11,0.12)",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: "1.1rem", cursor: total < 2 ? "default" : "pointer",
                opacity: total < 2 ? 0.3 : 1, transition: "all 0.2s",
                boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
              }}
                onMouseEnter={e => { e.currentTarget.style.background="#8A1613"; e.currentTarget.style.color="#fff"; e.currentTarget.style.borderColor="#8A1613"; }}
                onMouseLeave={e => { e.currentTarget.style.background="#fff"; e.currentTarget.style.color="inherit"; e.currentTarget.style.borderColor="rgba(11,11,11,0.12)"; }}
              >←</button>

              {/* Dot indicators */}
              <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                {testimonials.map((_, i) => (
                  <button key={i} onClick={() => setCurrent(i)} aria-label={`Testimonial ${i + 1}`} style={{
                    width: i === current ? 28 : 10, height: 10,
                    borderRadius: 100, border: "none", padding: 0,
                    background: i === current ? "#8A1613" : "rgba(11,11,11,0.18)",
                    cursor: "pointer", transition: "all 0.3s ease",
                  }} />
                ))}
              </div>

              {/* Next arrow */}
              <button onClick={next} aria-label="Next" disabled={total < 2} style={{
                width: 48, height: 48, borderRadius: "50%",
                background: "#fff", border: "1px solid rgba(11,11,11,0.12)",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: "1.1rem", cursor: total < 2 ? "default" : "pointer",
                opacity: total < 2 ? 0.3 : 1, transition: "all 0.2s",
                boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
              }}
                onMouseEnter={e => { e.currentTarget.style.background="#8A1613"; e.currentTarget.style.color="#fff"; e.currentTarget.style.borderColor="#8A1613"; }}
                onMouseLeave={e => { e.currentTarget.style.background="#fff"; e.currentTarget.style.color="inherit"; e.currentTarget.style.borderColor="rgba(11,11,11,0.12)"; }}
              >→</button>
            </div>

            {/* Counter */}
            <p style={{
              textAlign: "center", marginTop: "1rem",
              fontFamily: "var(--font)", fontSize: "0.75rem",
              color: "rgba(11,11,11,0.3)", letterSpacing: "0.1em",
            }}>
              {current + 1} / {total}
            </p>
          </>
        )}
      </section>

      {/* CTA */}
      <section style={{ background: "#0B0B0B", padding: "5rem 2rem", textAlign: "center" }}>
        <div className="container-sm">
          <h2 style={{
            fontFamily: "var(--font)", fontWeight: 900,
            fontSize: "clamp(1.8rem,4vw,2.8rem)",
            color: "#fff", marginBottom: "1rem", letterSpacing: "-0.02em",
          }}>
            Want to work with us?
          </h2>
          <p style={{ color: "rgba(255,255,255,0.5)", fontFamily: "var(--font-body)", marginBottom: "2rem" }}>
            Let's build a health campaign that makes a real difference.
          </p>
          <Link to="/contact" className="btn-red">Plan a Health Campaign</Link>
        </div>
      </section>

    </div>
  );
}
